import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { notesService } from '../../services/notesService';
import { AuthorStatusBadge, toAuthorStatus } from '../../utils/authorStatus';

// Types
interface Note {
  _id: string;
  note: string;
  createdAt: string;
  /** The API only ever sends an author 'approved' or 'pending'. */
  moderation?: {
    status: string;
  };
  heart_count: number;
  display?: {
    viewCount: number;
  };
  tags?: string[];
  categories?: string[];
  location_city?: string;
  location_state?: string;
}

interface NoteImpact {
  totalViews: number;
  uniqueDisplaysCount: number;
  displayLocationsSummary: Array<{city: string; state: string}>;
  inspiredNotesCount: number;
  inspirationChainLength: number;
  totalHearts: number;
  totalQrScans: number;
}

interface UserStats {
  totalNotes: number;
  totalViews: number;
  totalHearts: number;
  totalInspired: number;
  pendingNotes: number;
  approvedNotes: number;
  topCategories: Array<{name: string; count: number}>;
  topTags?: Array<{name: string; count: number}>;
  totalDisplays?: number;
}

const UserNotesDashboard: React.FC = () => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'approved'>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'views' | 'hearts'>('newest');
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [selectedNoteImpact, setSelectedNoteImpact] = useState<NoteImpact | null>(null);

// Load user's notes and stats
  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Load notes
        const userNotes = await notesService.getMyNotes();
        setNotes(userNotes);
        
        // Load user stats
        const userStats = await notesService.getUserStats();
        setStats(userStats);
      } catch (err) {
        setError('Failed to load dashboard data. Please try again later.');
        console.error('Error loading dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadDashboardData();
  }, []);
  
  // Load note impact when a note is selected
  useEffect(() => {
    if (!selectedNoteId) {
      setSelectedNoteImpact(null);
      return;
    }
    
    const loadNoteImpact = async () => {
      try {
        const impact = await notesService.getNoteImpact(selectedNoteId);
        setSelectedNoteImpact(impact);
      } catch (err) {
        console.error('Error loading note impact:', err);
        setSelectedNoteImpact(null);
      }
    };
    
    loadNoteImpact();
  }, [selectedNoteId]);
  
  // Filter notes based on active tab. This is the author's own view, so it
  // only knows approved and pending (see utils/authorStatus).
  const filteredNotes = notes.filter(note => {
    if (activeTab === 'all') return true;
    return toAuthorStatus(note.moderation?.status) === activeTab;
  });
  
  // Sort filtered notes
  const sortedNotes = [...filteredNotes].sort((a, b) => {
    if (sortBy === 'newest') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    if (sortBy === 'oldest') return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    if (sortBy === 'views') return (b.display?.viewCount || 0) - (a.display?.viewCount || 0);
    if (sortBy === 'hearts') return b.heart_count - a.heart_count;
    return 0;
  });
  
  // Render status badge: Approved or Pending only
  const renderStatusBadge = (status?: string) => <AuthorStatusBadge status={status} />;

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  
  if (loading) {
    return (
      <div className="text-center py-5" data-testid="loading-state">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading your dashboard...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="alert alert-danger" role="alert" data-testid="error-state">
        {error}
      </div>
    );
  }
  
  return (
    <div className="user-notes-dashboard" data-testid="dashboard-container">
      <h2 className="mb-4">My Notes Dashboard</h2>
      
      {/* Stats Overview */}
      {stats && (
        <div className="row mb-4" data-testid="stats-cards">
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card h-100 bg-light">
              <div className="card-body text-center">
                <h3 className="display-4" data-testid="total-notes">{stats.totalNotes}</h3>
                <p className="card-text text-muted">Total Notes</p>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card h-100 bg-light">
              <div className="card-body text-center">
                <h3 className="display-4" data-testid="total-views">{stats.totalViews}</h3>
                <p className="card-text text-muted">Total Views</p>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card h-100 bg-light">
              <div className="card-body text-center">
                <h3 className="display-4" data-testid="total-hearts">{stats.totalHearts}</h3>
                <p className="card-text text-muted">Total Hearts</p>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card h-100 bg-light">
              <div className="card-body text-center">
                <h3 className="display-4" data-testid="total-inspired">{stats.totalInspired}</h3>
                <p className="card-text text-muted">Inspired Notes</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="row mb-4">
        {/* Notes List Section */}
        <div className="col-lg-8 mb-4">
          <div className="card">
            <div className="card-header bg-white">
              <ul className="nav nav-tabs card-header-tabs">
                <li className="nav-item">
                  <button 
                    className={`nav-link ${activeTab === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveTab('all')}
                    data-testid="tab-all"
                  >
                    All Notes
                    {stats && <span className="badge bg-secondary ms-2">{stats.totalNotes}</span>}
                  </button>
                </li>
                <li className="nav-item">
                  <button 
                    className={`nav-link ${activeTab === 'pending' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pending')}
                    data-testid="tab-pending"
                  >
                    Pending
                    {stats && <span className="badge bg-warning text-dark ms-2">{stats.pendingNotes}</span>}
                  </button>
                </li>
                <li className="nav-item">
                  <button 
                    className={`nav-link ${activeTab === 'approved' ? 'active' : ''}`}
                    onClick={() => setActiveTab('approved')}
                    data-testid="tab-approved"
                  >
                    Approved
                    {stats && <span className="badge bg-success ms-2">{stats.approvedNotes}</span>}
                  </button>
                </li>
              </ul>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <span className="me-2">Sort by:</span>
                  <div className="btn-group" data-testid="sort-controls">
                    <button 
                      className={`btn btn-sm ${sortBy === 'newest' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setSortBy('newest')}
                      data-testid="sort-newest"
                    >
                      Newest
                    </button>
                    <button 
                      className={`btn btn-sm ${sortBy === 'oldest' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setSortBy('oldest')}
                      data-testid="sort-oldest"
                    >
                      Oldest
                    </button>
                    <button 
                      className={`btn btn-sm ${sortBy === 'views' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setSortBy('views')}
                      data-testid="sort-views"
                    >
                      Most Viewed
                    </button>
                    <button 
                      className={`btn btn-sm ${sortBy === 'hearts' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setSortBy('hearts')}
                      data-testid="sort-hearts"
                    >
                      Most Hearted
                    </button>
                  </div>
                </div>
                <Link to="/my-notes/new" className="btn btn-primary" data-testid="new-note-btn">
                  <i className="fas fa-plus-circle me-1"></i> New Note
                </Link>
              </div>
              
              {sortedNotes.length === 0 ? (
                <div className="text-center py-4" data-testid="empty-notes-state">
                  <p className="text-muted">No notes found in this category.</p>
                  <Link to="/my-notes/new" className="btn btn-primary">
                    Submit Your First Note
                  </Link>
                </div>
              ) : (
                <div className="list-group" data-testid="notes-list">
                  {sortedNotes.map(note => (
                    <button
                      key={note._id}
                      className={`list-group-item list-group-item-action ${selectedNoteId === note._id ? 'active' : ''}`}
                      onClick={() => setSelectedNoteId(note._id === selectedNoteId ? null : note._id)}
                      data-testid={`note-item-${note._id}`}
                    >
                      <div className="d-flex w-100 justify-content-between align-items-center">
                        <h5 className="mb-1">
                          {note.note.length > 50 ? `${note.note.substring(0, 50)}...` : note.note}
                        </h5>
                        <span>
                          {renderStatusBadge(note.moderation?.status)}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          Created: {formatDate(note.createdAt)}
                        </small>
                        <div>
                          <span className="me-3" title="Hearts">
                            <i className="fas fa-heart text-danger"></i> {note.heart_count}
                          </span>
                          {note.display && (
                            <span title="Views">
                              <i className="fas fa-eye"></i> {note.display.viewCount}
                            </span>
                          )}
                        </div>
                      </div>
                      {note.tags && note.tags.length > 0 && (
                        <div className="mt-2">
                          {note.tags.map(tag => (
                            <span key={tag} className="badge bg-secondary me-1">{tag}</span>
                          ))}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Impact Analytics Section */}
        <div className="col-lg-4">
          <div className="card mb-4" data-testid="top-categories">
            <div className="card-header">
              <h5 className="card-title mb-0">Top Categories</h5>
            </div>
            <div className="card-body">
              {stats?.topCategories.length ? (
                <div className="list-group list-group-flush">
                  {stats.topCategories.map(category => (
                    <div key={category.name} className="list-group-item d-flex justify-content-between align-items-center">
                      {category.name}
                      <span className="badge bg-primary rounded-pill">{category.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted">No categories found</p>
              )}
            </div>
          </div>
          
          <div className="card mb-4" data-testid="top-tags">
            <div className="card-header">
              <h5 className="card-title mb-0">Top Tags</h5>
            </div>
            <div className="card-body">
              {stats?.topTags?.length ? (
                <div className="list-group list-group-flush">
                  {stats.topTags.map(tag => (
                    <div key={tag.name} className="list-group-item d-flex justify-content-between align-items-center">
                      #{tag.name}
                      <span className="badge bg-primary rounded-pill">{tag.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted">No tags found</p>
              )}
            </div>
          </div>
          
          {/* Selected Note Impact */}
          {selectedNoteId && selectedNoteImpact && (
            <div className="card" data-testid="note-impact-details">
              <div className="card-header">
                <h5 className="card-title mb-0">Note Impact Details</h5>
              </div>
              <div className="card-body">
                <ul className="list-group list-group-flush">
                  <li className="list-group-item d-flex justify-content-between" data-testid="impact-views">
                    <span>Views</span>
                    <span>{selectedNoteImpact.totalViews}</span>
                  </li>
                  <li className="list-group-item d-flex justify-content-between" data-testid="impact-hearts">
                    <span>Hearts</span>
                    <span>{selectedNoteImpact.totalHearts}</span>
                  </li>
                  <li className="list-group-item d-flex justify-content-between" data-testid="impact-qr-scans">
                    <span>QR Scans</span>
                    <span>{selectedNoteImpact.totalQrScans}</span>
                  </li>
                  <li className="list-group-item d-flex justify-content-between" data-testid="impact-displays">
                    <span>Unique Displays</span>
                    <span>{selectedNoteImpact.uniqueDisplaysCount}</span>
                  </li>
                  <li className="list-group-item d-flex justify-content-between" data-testid="impact-inspired">
                    <span>Inspired Notes</span>
                    <span>{selectedNoteImpact.inspiredNotesCount}</span>
                  </li>
                </ul>
                
                {selectedNoteImpact.displayLocationsSummary.length > 0 && (
                  <>
                    <h6 className="mt-3 mb-2">Displayed Locations</h6>
                    <div className="location-tags" data-testid="impact-locations">
                      {selectedNoteImpact.displayLocationsSummary.map((location, index) => (
                        <span key={index} className="badge bg-info text-dark me-1 mb-1">
                          {location.city}, {location.state}
                        </span>
                      ))}
                    </div>
                  </>
                )}
                
                <div className="d-grid gap-2 mt-3">
                  <Link 
                    to={`/my-notes/${selectedNoteId}`} 
                    className="btn btn-outline-primary"
                    data-testid="view-note-details"
                  >
                    View Complete Details
                  </Link>
                  <Link 
                    to={`/my-notes/${selectedNoteId}/impact`} 
                    className="btn btn-outline-info"
                    data-testid="view-note-impact"
                  >
                    View Full Impact Analysis
                  </Link>
                  <Link 
                    to={`/my-notes/edit/${selectedNoteId}`} 
                    className="btn btn-outline-secondary"
                    data-testid="edit-note"
                  >
                    Edit Note
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserNotesDashboard; 